import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon,
  SwapHoriz as MoveIcon,
  LocalOffer as PriceIcon,
  CheckCircle as StatusIcon,
} from '@mui/icons-material'

export interface BulkAction {
  id: string
  label: string
  icon: React.ReactElement
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  variant?: 'contained' | 'outlined' | 'text'
  onClick: (selectedIds: string[]) => void
  disabled?: boolean
  requiresConfirmation?: boolean
  confirmationMessage?: string
}

interface BulkActionBarProps {
  selectedItems: string[]
  totalItems: number
  onSelectAll: (selected: boolean) => void
  onClearSelection: () => void
  actions: BulkAction[]
  itemName?: string // e.g., "products", "customers", "orders"
  showSelectAll?: boolean
  maxVisibleActions?: number
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedItems,
  totalItems,
  onSelectAll,
  onClearSelection,
  actions,
  itemName = 'items',
  showSelectAll = true,
  maxVisibleActions = 3,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const isAllSelected = selectedItems.length > 0 && selectedItems.length === totalItems
  const isIndeterminate = selectedItems.length > 0 && selectedItems.length < totalItems

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(event.target.checked)
  }

  const handleActionClick = (action: BulkAction) => {
    if (selectedItems.length === 0) return

    if (action.requiresConfirmation) {
      const message = action.confirmationMessage || 
        `Are you sure you want to ${action.label.toLowerCase()} ${selectedItems.length} ${itemName}?`
      
      if (window.confirm(message)) {
        action.onClick(selectedItems)
      }
    } else {
      action.onClick(selectedItems)
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const visibleActions = actions.slice(0, maxVisibleActions)
  const menuActions = actions.slice(maxVisibleActions)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {showSelectAll && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
                disabled={totalItems === 0}
              />
            }
            label={
              selectedItems.length > 0 
                ? `${selectedItems.length} of ${totalItems} ${itemName} selected`
                : `Select All (${totalItems} ${itemName})`
            }
            sx={{ 
              m: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                whiteSpace: 'nowrap'
              }
            }}
          />
        )}
        
        {selectedItems.length > 0 && (
          <Chip 
            label={`${selectedItems.length} selected`} 
            color="primary" 
            variant="outlined"
            onDelete={onClearSelection}
            size="small"
          />
        )}
      </Box>

      {selectedItems.length > 0 && (
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1 }}
        >
          {visibleActions.map((action) => (
            <Tooltip key={action.id} title={action.label}>
              <span>
                <Button
                  variant={action.variant || 'outlined'}
                  color={action.color || 'primary'}
                  size="small"
                  startIcon={action.icon}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                  sx={{ 
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto'
                  }}
                >
                  {action.label}
                </Button>
              </span>
            </Tooltip>
          ))}

          {menuActions.length > 0 && (
            <>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{ 
                  bgcolor: 'primary.50',
                  '&:hover': { bgcolor: 'primary.100' }
                }}
              >
                <MoreVertIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 200 }
                }}
              >
                {menuActions.map((action) => (
                  <MenuItem
                    key={action.id}
                    onClick={() => {
                      handleActionClick(action)
                      handleMenuClose()
                    }}
                    disabled={action.disabled}
                  >
                    <ListItemIcon sx={{ color: `${action.color || 'primary'}.main` }}>
                      {action.icon}
                    </ListItemIcon>
                    <ListItemText>{action.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Stack>
      )}
    </Box>
  )
}

// Common bulk actions for different entity types
export const getProductBulkActions = (
  onMove: (ids: string[]) => void,
  onPriceUpdate: (ids: string[]) => void,
  onStatusUpdate: (ids: string[]) => void,
  onDelete: (ids: string[]) => void,
  onExport: (ids: string[]) => void
): BulkAction[] => [
  {
    id: 'move',
    label: 'Move Category',
    icon: <MoveIcon />,
    onClick: onMove,
  },
  {
    id: 'price',
    label: 'Update Prices',
    icon: <PriceIcon />,
    onClick: onPriceUpdate,
  },
  {
    id: 'status',
    label: 'Update Status',
    icon: <StatusIcon />,
    onClick: onStatusUpdate,
  },
  {
    id: 'export',
    label: 'Export',
    icon: <DownloadIcon />,
    onClick: onExport,
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: <DeleteIcon />,
    color: 'error',
    onClick: onDelete,
    requiresConfirmation: true,
  },
]

export const getCustomerBulkActions = (
  onGroupUpdate: (ids: string[]) => void,
  onActivate: (ids: string[]) => void,
  onDeactivate: (ids: string[]) => void,
  onExport: (ids: string[]) => void,
  onDelete: (ids: string[]) => void
): BulkAction[] => [
  {
    id: 'group',
    label: 'Update Group',
    icon: <EditIcon />,
    onClick: onGroupUpdate,
  },
  {
    id: 'activate',
    label: 'Activate',
    icon: <VisibilityIcon />,
    color: 'success',
    onClick: onActivate,
  },
  {
    id: 'deactivate',
    label: 'Deactivate',
    icon: <VisibilityOffIcon />,
    color: 'warning',
    onClick: onDeactivate,
  },
  {
    id: 'export',
    label: 'Export',
    icon: <DownloadIcon />,
    onClick: onExport,
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: <DeleteIcon />,
    color: 'error',
    onClick: onDelete,
    requiresConfirmation: true,
  },
]

export const getOrderBulkActions = (
  onStatusUpdate: (ids: string[]) => void,
  onExport: (ids: string[]) => void,
  onCancel: (ids: string[]) => void
): BulkAction[] => [
  {
    id: 'status',
    label: 'Update Status',
    icon: <StatusIcon />,
    onClick: onStatusUpdate,
  },
  {
    id: 'export',
    label: 'Export',
    icon: <DownloadIcon />,
    onClick: onExport,
  },
  {
    id: 'cancel',
    label: 'Cancel Orders',
    icon: <ArchiveIcon />,
    color: 'error',
    onClick: onCancel,
    requiresConfirmation: true,
    confirmationMessage: 'Are you sure you want to cancel the selected orders? This action cannot be undone.',
  },
]

export default BulkActionBar